'use client'

import { CreditBadgeCard } from '@/components/marketing/tools/credit-badge-card'
import { useCreditBalance } from '@/hooks/use-billing'
import { useCreditStore } from '@/stores/credit-store'

/**
 * The credit card every public tool studio puts above its prompt.
 *
 * It lived three times — once in `fal-tool-studio`, once in each Seedance
 * studio — and the copies drifted where it costs money: the Seedance ones
 * called the billing dialog themselves with no checkout return address, so a
 * visitor who upgraded from this card came back from Stripe on `/` instead of
 * the page they were generating on. Upgrading is what this card is FOR, so the
 * one thing it must not own is where checkout returns to. The caller passes
 * `onUpgrade` and every branch routes through it.
 *
 * This module is the data shell; the rendering lives in `credit-badge-card`.
 */

/** Live credit badge — only mounted when signed in so the balance fetch never
 *  fires for logged-out visitors. */
export function CreditBadge({
  cost,
  costLabel,
  onBuyCredits,
  onUpgrade,
  planBlocked,
  previewMode,
}: {
  cost: number
  costLabel?: string
  onBuyCredits?: () => void
  onUpgrade: () => void
  planBlocked: boolean
  previewMode: boolean
}) {
  useCreditBalance() // triggers the SWR fetch + syncs the store
  const balance = useCreditStore(s => s.balance)
  const planName = useCreditStore(s => s.planName)
  const planSlug = useCreditStore(s => s.planSlug)

  if (balance === null) return null

  return (
    <CreditBadgeCard
      balance={balance}
      cost={cost}
      costLabel={costLabel}
      onBuyCredits={onBuyCredits}
      onUpgrade={onUpgrade}
      planBlocked={planBlocked}
      planName={planName}
      planSlug={planSlug}
      previewMode={previewMode}
    />
  )
}
